/* ==========================================================================
   AMACH Studio — theme scripts
   Vanilla JS, no dependencies.
   ========================================================================== */
(function () {
  'use strict';

  var theme = window.theme || {};
  var routes = theme.routes || {};
  var strings = theme.strings || {};

  /* ---------- Helpers ---------- */
  function $(selector, scope) {
    return (scope || document).querySelector(selector);
  }

  function $$(selector, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(selector));
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments;
      var ctx = this;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  function parseHTML(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  function sectionInnerHTML(html, selector) {
    var doc = parseHTML(html);
    var el = doc.querySelector(selector);
    return el ? el.innerHTML : null;
  }

  function focusableIn(el) {
    return $$(
      'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])',
      el
    ).filter(function (node) {
      return node.offsetParent !== null || node === document.activeElement;
    });
  }

  /* ---------- Header measurements ---------- */
  function setHeaderVars() {
    var header = $('[data-header]');
    if (!header) return;
    var rect = header.getBoundingClientRect();
    var total = rect.bottom + window.scrollY;
    document.documentElement.style.setProperty('--header-height', Math.round(rect.height) + 'px');
    document.documentElement.style.setProperty('--header-total', Math.round(total) + 'px');
  }

  /* ==========================================================================
     Drawers (menu, search, bag, filters, size guide)
     ========================================================================== */
  var Drawers = {
    active: null,
    trigger: null,

    overlay: function () {
      return $('[data-page-overlay]');
    },

    open: function (id, trigger, options) {
      var drawer = document.getElementById(id);
      if (!drawer) return false;
      var opts = options || {};
      if (this.active && this.active !== drawer) this.close(true);

      this.active = drawer;
      this.trigger = trigger || document.activeElement;
      this.hoverOpened = !!opts.hover;
      this.openedAt = Date.now();

      // Panels that open over the header (mega menu, search) line up with it.
      if (drawer.hasAttribute('data-align-header')) {
        var header = $('[data-header]');
        var top = header ? Math.max(0, header.getBoundingClientRect().top) : 0;
        drawer.style.setProperty('--drawer-top', Math.round(top) + 'px');
      }

      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      $$('[aria-controls="' + id + '"]').forEach(function (btn) {
        btn.setAttribute('aria-expanded', 'true');
      });

      var overlay = this.overlay();
      if (overlay) {
        overlay.hidden = false;
        requestAnimationFrame(function () {
          overlay.classList.add('is-visible');
        });
      }
      if (!opts.hover) document.body.classList.add('is-locked');

      if (!opts.hover) {
        setTimeout(function () {
          var target = drawer.querySelector('[data-search-input]');
          if (target) {
            target.focus({ preventScroll: true });
          } else {
            drawer.focus({ preventScroll: true });
          }
        }, 60);
      }

      drawer.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
      return true;
    },

    close: function (silent) {
      var drawer = this.active;
      if (!drawer) return;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      $$('[aria-controls="' + drawer.id + '"]').forEach(function (btn) {
        btn.setAttribute('aria-expanded', 'false');
      });
      this.active = null;

      if (!silent) {
        var overlay = this.overlay();
        if (overlay) {
          overlay.classList.remove('is-visible');
          setTimeout(function () {
            if (!Drawers.active) overlay.hidden = true;
          }, 300);
        }
        document.body.classList.remove('is-locked');
        if (!this.hoverOpened && this.trigger && typeof this.trigger.focus === 'function') {
          this.trigger.focus({ preventScroll: true });
        }
      }
      this.hoverOpened = false;
      drawer.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
    },

    trap: function (event) {
      if (!this.active || event.key !== 'Tab') return;
      var nodes = focusableIn(this.active);
      if (!nodes.length) return;
      var first = nodes[0];
      var last = nodes[nodes.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.active)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  window.AmachDrawers = Drawers;

  document.addEventListener('click', function (event) {
    var opener = event.target.closest('[data-drawer-open]');
    if (opener) {
      var id = opener.getAttribute('data-drawer-open');
      if (document.getElementById(id)) {
        event.preventDefault();
        if (Drawers.active && Drawers.active.id === id) {
          // A click straight after the menu opened on hover keeps it open.
          if (Drawers.hoverOpened && Date.now() - Drawers.openedAt < 700) {
            Drawers.hoverOpened = false;
            document.body.classList.add('is-locked');
            return;
          }
          Drawers.close();
        } else {
          Drawers.open(id, opener);
        }
        return;
      }
    }

    if (event.target.closest('[data-drawer-close]') || event.target.matches('[data-page-overlay]')) {
      event.preventDefault();
      Drawers.close();
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && Drawers.active) {
      Drawers.close();
    } else {
      Drawers.trap(event);
    }
  });

  /* ==========================================================================
     Announcement rotation
     ========================================================================== */
  function initAnnouncements() {
    $$('[data-announcement-rotate]').forEach(function (bar) {
      var slides = $$('.announcement__slide', bar);
      if (slides.length < 2) return;
      var index = 0;
      setInterval(function () {
        slides[index].classList.remove('is-active');
        slides[index].setAttribute('aria-hidden', 'true');
        index = (index + 1) % slides.length;
        slides[index].classList.add('is-active');
        slides[index].removeAttribute('aria-hidden');
      }, 5000);
    });
  }

  /* ==========================================================================
     Predictive search
     ========================================================================== */
  if (!customElements.get('predictive-search')) {
    customElements.define(
      'predictive-search',
      class PredictiveSearch extends HTMLElement {
        connectedCallback() {
          if (this._ready) return;
          this._ready = true;
          this.input = this.querySelector('[data-search-input]');
          this.results = this.querySelector('[data-search-results]');
          this.enabled = this.getAttribute('data-enabled') === 'true';
          this.controller = null;
          if (!this.input || !this.enabled) return;
          this.input.addEventListener(
            'input',
            debounce(this.onInput.bind(this), 260)
          );
        }

        onInput() {
          var term = this.input.value.trim();
          if (!term.length) {
            this.results.innerHTML = '';
            return;
          }
          if (this.controller) this.controller.abort();
          this.controller = new AbortController();
          var url =
            (routes.predictiveSearch || '/search/suggest') +
            '?q=' +
            encodeURIComponent(term) +
            '&section_id=predictive-search' +
            '&resources[type]=product,collection,page,query' +
            '&resources[limit]=6' +
            '&resources[options][unavailable_products]=last';
          var self = this;
          fetch(url, { signal: this.controller.signal })
            .then(function (res) {
              if (!res.ok) throw new Error(res.status);
              return res.text();
            })
            .then(function (html) {
              var inner = sectionInnerHTML(html, '#shopify-section-predictive-search');
              self.results.innerHTML = inner || '';
            })
            .catch(function (error) {
              if (error.name !== 'AbortError') self.results.innerHTML = '';
            });
        }
      }
    );
  }

  /* ==========================================================================
     Bag (cart) API
     ========================================================================== */
  var Cart = {
    sectionIds: function () {
      var ids = [];
      if ($('#CartDrawer')) ids.push('cart-drawer');
      var page = $('cart-page');
      if (page && page.dataset.sectionId) ids.push(page.dataset.sectionId);
      return ids;
    },

    updateCount: function (count) {
      var n = parseInt(count, 10) || 0;
      $$('[data-cart-count]').forEach(function (el) {
        el.textContent = el.hasAttribute('data-cart-count-pad') && n < 10 ? '0' + n : String(n);
        if (el.hasAttribute('data-count')) el.setAttribute('data-count', n);
      });
    },

    render: function (sections) {
      if (!sections) return;
      var drawerHTML = sections['cart-drawer'];
      if (drawerHTML) {
        var doc = parseHTML(drawerHTML);
        var fresh = doc.querySelector('[data-cart-drawer-inner]');
        var current = $('#CartDrawer [data-cart-drawer-inner]');
        if (fresh && current) {
          current.replaceWith(fresh);
          Cart.updateCount(fresh.getAttribute('data-cart-count-value'));
        }
      }
      var page = $('cart-page');
      if (page && sections[page.dataset.sectionId]) {
        var pageDoc = parseHTML(sections[page.dataset.sectionId]);
        var freshPage = pageDoc.querySelector('cart-page');
        if (freshPage) page.innerHTML = freshPage.innerHTML;
        page.classList.remove('is-loading');
      }
    },

    refreshCount: function () {
      return fetch((routes.root || '/') + 'cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) {
          return r.json();
        })
        .then(function (cart) {
          Cart.updateCount(cart.item_count);
          return cart;
        });
    },

    add: function (formData) {
      var ids = this.sectionIds();
      if (ids.length) {
        formData.append('sections', ids.join(','));
        formData.append('sections_url', window.location.pathname);
      }
      return fetch(routes.cartAdd + '.js', {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      }).then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok || data.status) {
            var err = new Error(data.description || data.message || strings.cartError);
            err.data = data;
            throw err;
          }
          return data;
        });
      });
    },

    change: function (line, quantity) {
      var body = {
        line: line,
        quantity: quantity,
        sections: this.sectionIds(),
        sections_url: window.location.pathname
      };
      return fetch(routes.cartChange + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      }).then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok || data.status) {
            var err = new Error(data.description || data.message || strings.cartError);
            err.data = data;
            throw err;
          }
          return data;
        });
      });
    },

    updateNote: function (note) {
      return fetch(routes.cartUpdate + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note: note })
      });
    }
  };

  window.AmachCart = Cart;

  /* Line quantity / remove (drawer + page) */
  function changeLine(lineEl, quantity) {
    var line = parseInt(lineEl.getAttribute('data-cart-line'), 10);
    lineEl.classList.add('is-loading');
    Cart.change(line, quantity)
      .then(function (data) {
        Cart.render(data.sections);
        Cart.updateCount(data.item_count);
      })
      .catch(function (error) {
        lineEl.classList.remove('is-loading');
        window.alert(error.message || strings.cartError);
      });
  }

  document.addEventListener('click', function (event) {
    var step = event.target.closest('[data-qty-step]');
    if (step) {
      var wrapper = step.closest('[data-qty]');
      var input = wrapper && wrapper.querySelector('.qty__input');
      if (!input) return;
      event.preventDefault();
      var increment = parseInt(input.getAttribute('step'), 10) || 1;
      var min = parseInt(input.getAttribute('min'), 10) || 0;
      var max = input.getAttribute('max') ? parseInt(input.getAttribute('max'), 10) : null;
      var next = (parseInt(input.value, 10) || 0) + parseInt(step.getAttribute('data-qty-step'), 10) * increment;
      if (next < min) next = min;
      if (max !== null && next > max) next = max;
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    var remove = event.target.closest('[data-line-remove]');
    if (remove) {
      var lineEl = remove.closest('[data-cart-line]');
      if (!lineEl) return;
      event.preventDefault();
      changeLine(lineEl, 0);
    }
  });

  document.addEventListener('change', function (event) {
    var input = event.target;
    if (input.matches('.cart-line .qty__input')) {
      var lineEl = input.closest('[data-cart-line]');
      var qty = parseInt(input.value, 10);
      if (isNaN(qty) || qty < 0) qty = 0;
      changeLine(lineEl, qty);
    }
    if (input.matches('[data-cart-note]')) {
      Cart.updateNote(input.value);
    }
    if (input.matches('[data-autosubmit]') && input.form) {
      input.form.submit();
    }
    if (input.matches('[data-sort-select]')) {
      var url = new URL(window.location.href);
      url.searchParams.set('sort_by', input.value);
      url.searchParams.delete('page');
      window.location.href = url.toString();
    }
  });

  /* ==========================================================================
     Product form
     ========================================================================== */
  if (!customElements.get('product-form')) {
    customElements.define(
      'product-form',
      class ProductForm extends HTMLElement {
        connectedCallback() {
          this.form = this.querySelector('form');
          if (!this.form) return;
          this.button = this.querySelector('[data-add-button]');
          this.label = this.querySelector('[data-add-label]');
          this.error = this.querySelector('[data-form-error]');
          this.form.addEventListener('submit', this.onSubmit.bind(this));
        }

        onSubmit(event) {
          if (this.getAttribute('data-cart-type') !== 'drawer' || !$('#CartDrawer')) return;
          event.preventDefault();
          if (!this.button || this.button.hasAttribute('disabled')) return;

          var self = this;
          var previous = this.label ? this.label.textContent : '';
          this.button.classList.add('is-loading');
          this.button.setAttribute('aria-busy', 'true');
          if (this.label) this.label.textContent = strings.adding || previous;
          if (this.error) this.error.hidden = true;

          Cart.add(new FormData(this.form))
            .then(function (data) {
              Cart.render(data.sections);
              if (!data.sections) Cart.refreshCount();
              Drawers.open('CartDrawer', self.button);
            })
            .catch(function (error) {
              if (self.error) {
                self.error.textContent = error.message;
                self.error.hidden = false;
              }
            })
            .finally(function () {
              self.button.classList.remove('is-loading');
              self.button.removeAttribute('aria-busy');
              if (self.label) self.label.textContent = previous;
            });
        }
      }
    );
  }

  /* ==========================================================================
     Variant picker
     ========================================================================== */
  if (!customElements.get('variant-picker')) {
    customElements.define(
      'variant-picker',
      class VariantPicker extends HTMLElement {
        connectedCallback() {
          var json = this.querySelector('[data-variants]');
          this.variants = json ? JSON.parse(json.textContent) : [];
          this.fieldsets = $$('fieldset.option', this);
          this.section = this.closest('product-page');
          this.addEventListener('change', this.onChange.bind(this));
          this.updateAvailability();
        }

        selectedOptions() {
          return this.fieldsets.map(function (fieldset) {
            var checked = fieldset.querySelector('input:checked');
            return checked ? checked.value : null;
          });
        }

        currentVariant() {
          var selected = this.selectedOptions();
          return (
            this.variants.find(function (variant) {
              return variant.options.every(function (value, i) {
                return value === selected[i];
              });
            }) || null
          );
        }

        onChange(event) {
          var fieldset = event.target.closest('fieldset.option');
          if (fieldset) {
            var label = fieldset.querySelector('[data-selected-value]');
            if (label) label.textContent = event.target.value;
          }
          var variant = this.currentVariant();
          this.updateAvailability();
          this.updateForm(variant);
          if (!variant) return;
          this.updateURL(variant);
          this.updatePrice(variant);
          this.updateMedia(variant);
          document.dispatchEvent(new CustomEvent('variant:change', { detail: { variant: variant, section: this.section } }));
        }

        updateAvailability() {
          var selected = this.selectedOptions();
          var variants = this.variants;
          this.fieldsets.forEach(function (fieldset, index) {
            $$('input', fieldset).forEach(function (input) {
              var available = variants.some(function (variant) {
                if (!variant.available || variant.options[index] !== input.value) return false;
                return variant.options.every(function (value, i) {
                  return i === index || selected[i] === null || value === selected[i];
                });
              });
              var label = input.nextElementSibling;
              if (label) label.classList.toggle('is-unavailable', !available);
            });
          });
        }

        updateURL(variant) {
          if (!window.history.replaceState) return;
          var url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        }

        updateForm(variant) {
          var formId = this.getAttribute('data-product-form');
          var form = formId ? document.getElementById(formId) : null;
          if (!form) return;
          var idInput = form.querySelector('[data-variant-id]');
          var button = form.querySelector('[data-add-button]');
          var label = form.querySelector('[data-add-label]');
          if (idInput && variant) idInput.value = variant.id;
          if (!button) return;
          if (!variant) {
            button.setAttribute('disabled', '');
            if (label) label.textContent = strings.unavailable;
          } else if (!variant.available) {
            button.setAttribute('disabled', '');
            if (label) label.textContent = strings.soldOut;
          } else {
            button.removeAttribute('disabled');
            if (label) label.textContent = strings.addToCart;
          }
        }

        updatePrice(variant) {
          // Ask Shopify for the rendered price so the shopper's currency (Markets) is always right.
          var target = this.section && this.section.querySelector('[data-price]');
          var productUrl = this.section && this.section.getAttribute('data-product-url');
          var sectionId = this.getAttribute('data-section-id');
          if (!target || !productUrl || !sectionId) return;
          if (this.priceRequest) this.priceRequest.abort();
          this.priceRequest = new AbortController();
          var join = productUrl.indexOf('?') > -1 ? '&' : '?';
          fetch(productUrl + join + 'variant=' + variant.id + '&section_id=' + encodeURIComponent(sectionId), { signal: this.priceRequest.signal })
            .then(function (res) {
              return res.text();
            })
            .then(function (html) {
              var fresh = parseHTML(html).querySelector('[data-price]');
              if (fresh) target.innerHTML = fresh.innerHTML;
            })
            .catch(function () {});
        }

        updateMedia(variant) {
          if (!variant.featured_media || !this.section) return;
          var list = this.section.querySelector('[data-media-list]');
          var item = list && list.querySelector('[data-media-id="' + variant.featured_media.id + '"]');
          if (!item) return;
          if (window.matchMedia('(min-width: 990px)').matches) {
            $$('[data-media-id]', list).forEach(function (el) {
              el.style.order = '';
            });
            item.style.order = '-1';
          } else {
            list.scrollTo({ left: item.offsetLeft, behavior: 'smooth' });
          }
        }
      }
    );
  }

  /* ==========================================================================
     Product page (gallery indicators, share)
     ========================================================================== */
  if (!customElements.get('product-page')) {
    customElements.define(
      'product-page',
      class ProductPage extends HTMLElement {
        connectedCallback() {
          this.list = this.querySelector('[data-media-list]');
          this.dots = $$('.product__dot', this);
          if (this.list && this.dots.length) {
            this.list.addEventListener('scroll', debounce(this.onScroll.bind(this), 60), { passive: true });
          }
          var share = this.querySelector('[data-share-url]');
          if (share) share.addEventListener('click', this.onShare.bind(this, share));
        }

        onScroll() {
          var width = this.list.clientWidth || 1;
          var index = Math.round(this.list.scrollLeft / width);
          this.dots.forEach(function (dot, i) {
            dot.classList.toggle('is-active', i === index);
          });
        }

        onShare(button) {
          var url = button.getAttribute('data-share-url');
          var title = button.getAttribute('data-share-title');
          if (navigator.share) {
            navigator.share({ url: url, title: title }).catch(function () {});
          } else if (navigator.clipboard) {
            navigator.clipboard.writeText(url).then(function () {
              var original = button.textContent;
              button.textContent = strings.linkCopied || original;
              setTimeout(function () {
                button.textContent = original;
              }, 2000);
            });
          }
        }
      }
    );
  }

  /* ==========================================================================
     Pickup availability
     ========================================================================== */
  if (!customElements.get('pickup-availability')) {
    customElements.define(
      'pickup-availability',
      class PickupAvailability extends HTMLElement {
        connectedCallback() {
          this.onVariant = this.onVariant.bind(this);
          document.addEventListener('variant:change', this.onVariant);
        }

        disconnectedCallback() {
          document.removeEventListener('variant:change', this.onVariant);
        }

        onVariant(event) {
          var section = event.detail.section;
          if (section && !section.contains(this)) return;
          var variant = event.detail.variant;
          var root = this.getAttribute('data-root-url') || '/';
          if (!root.endsWith('/')) root += '/';
          var self = this;
          fetch(root + 'variants/' + variant.id + '/?section_id=pickup-availability')
            .then(function (res) {
              return res.text();
            })
            .then(function (html) {
              var inner = sectionInnerHTML(html, '#shopify-section-pickup-availability');
              self.innerHTML = inner !== null ? inner : '';
            })
            .catch(function () {
              self.innerHTML = '';
            });
        }
      }
    );
  }

  /* ==========================================================================
     Product recommendations
     ========================================================================== */
  if (!customElements.get('product-recommendations')) {
    customElements.define(
      'product-recommendations',
      class ProductRecommendations extends HTMLElement {
        connectedCallback() {
          if (this.querySelector('.product-grid')) return;
          var self = this;
          var load = function () {
            fetch(self.getAttribute('data-url'))
              .then(function (res) {
                return res.text();
              })
              .then(function (html) {
                var doc = parseHTML(html);
                var fresh = doc.querySelector('product-recommendations');
                if (fresh && fresh.innerHTML.trim().length) {
                  self.innerHTML = fresh.innerHTML;
                }
              })
              .catch(function () {});
          };
          if ('IntersectionObserver' in window) {
            var observer = new IntersectionObserver(
              function (entries) {
                if (entries[0].isIntersecting) {
                  observer.disconnect();
                  load();
                }
              },
              { rootMargin: '0px 0px 600px 0px' }
            );
            observer.observe(this);
          } else {
            load();
          }
        }
      }
    );
  }

  /* ==========================================================================
     Collection tabs
     ========================================================================== */
  if (!customElements.get('collection-tabs')) {
    customElements.define(
      'collection-tabs',
      class CollectionTabs extends HTMLElement {
        connectedCallback() {
          this.tabs = $$('[data-tab]', this);
          this.panels = $$('[data-panel]', this);
          if (this.tabs.length < 2) return;
          var self = this;
          this.tabs.forEach(function (tab, index) {
            tab.addEventListener('click', function () {
              self.select(index);
            });
            tab.addEventListener('keydown', function (event) {
              if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
                event.preventDefault();
                var next = (index + (event.key === 'ArrowRight' ? 1 : -1) + self.tabs.length) % self.tabs.length;
                self.select(next);
                self.tabs[next].focus();
              }
            });
          });
        }

        select(index) {
          this.tabs.forEach(function (tab, i) {
            tab.classList.toggle('is-active', i === index);
            tab.setAttribute('aria-selected', i === index ? 'true' : 'false');
            tab.setAttribute('tabindex', i === index ? '0' : '-1');
          });
          this.panels.forEach(function (panel, i) {
            panel.hidden = i !== index;
          });
        }
      }
    );
  }

  /* ==========================================================================
     Collection links (hover image swap)
     ========================================================================== */
  if (!customElements.get('collection-links')) {
    customElements.define(
      'collection-links',
      class CollectionLinks extends HTMLElement {
        connectedCallback() {
          this.layers = $$('[data-hover-layer]', this);
          if (this.layers.length < 2) return;
          var self = this;
          $$('[data-hover-index]', this).forEach(function (link) {
            var show = function () {
              self.show(link.getAttribute('data-hover-index'));
            };
            link.addEventListener('mouseenter', show);
            link.addEventListener('focus', show);
          });
          this.addEventListener('mouseleave', function () {
            self.show('0');
          });
        }

        show(index) {
          var found = false;
          this.layers.forEach(function (layer) {
            var match = layer.getAttribute('data-hover-layer') === index;
            if (match) found = true;
            layer.classList.toggle('is-active', match);
          });
          if (!found) {
            this.layers.forEach(function (layer) {
              layer.classList.toggle('is-active', layer.getAttribute('data-hover-layer') === '0');
            });
          }
        }
      }
    );
  }

  /* ==========================================================================
     Filters, load more
     ========================================================================== */
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form.matches('[data-facets-form]')) return;
    event.preventDefault();
    var params = new URLSearchParams();
    new FormData(form).forEach(function (value, key) {
      if (String(value).trim() !== '') params.append(key, value);
    });
    var action = form.getAttribute('action') || window.location.pathname;
    window.location.href = action + (params.toString() ? '?' + params.toString() : '');
  });

  document.addEventListener('click', function (event) {
    var more = event.target.closest('[data-load-more]');
    if (!more) return;
    var section = more.closest('[data-section-id]');
    if (!section) return;
    event.preventDefault();
    var grid = section.querySelector('[data-product-grid]');
    var url = new URL(more.href, window.location.origin);
    url.searchParams.set('section_id', section.getAttribute('data-section-id'));
    more.classList.add('is-loading');
    more.textContent = strings.loading || more.textContent;
    fetch(url.toString())
      .then(function (res) {
        return res.text();
      })
      .then(function (html) {
        var doc = parseHTML(html);
        var freshGrid = doc.querySelector('[data-product-grid]');
        var freshPagination = doc.querySelector('[data-pagination]');
        if (freshGrid && grid) {
          Array.prototype.slice.call(freshGrid.children).forEach(function (item) {
            grid.appendChild(item);
          });
        }
        var pagination = section.querySelector('[data-pagination]');
        if (pagination) {
          if (freshPagination) pagination.replaceWith(freshPagination);
          else pagination.remove();
        }
        if (window.history.replaceState) {
          var pageUrl = new URL(more.href, window.location.origin);
          window.history.replaceState({}, '', pageUrl.pathname + pageUrl.search);
        }
      })
      .catch(function () {
        window.location.href = more.href;
      });
  });

  /* ==========================================================================
     Mega menu: open on hover (desktop), close when the pointer leaves
     ========================================================================== */
  function initMegaHover() {
    var canHover = window.matchMedia('(hover: hover) and (min-width: 990px)');
    var openTimer = null;
    var closeTimer = null;

    function activeMega() {
      return Drawers.active && Drawers.active.hasAttribute('data-mega') ? Drawers.active : null;
    }

    function scheduleClose(delay) {
      clearTimeout(closeTimer);
      closeTimer = setTimeout(function () {
        var mega = activeMega();
        if (mega && Drawers.hoverOpened) Drawers.close();
      }, delay);
    }

    document.addEventListener('mouseover', function (event) {
      if (!canHover.matches) return;
      var mega = activeMega();
      if (mega && mega.contains(event.target)) clearTimeout(closeTimer);

      var trigger = event.target.closest('[data-mega-trigger]');
      if (trigger) {
        var id = trigger.getAttribute('data-drawer-open');
        clearTimeout(openTimer);
        if (Drawers.active && Drawers.active.id === id) return;
        if (Drawers.active && !mega) return;
        openTimer = setTimeout(function () {
          if (Drawers.active && Drawers.active.id === id) return;
          if (Drawers.active && !activeMega()) return;
          Drawers.open(id, trigger, { hover: true });
        }, mega ? 60 : 160);
        return;
      }

      if (mega && Drawers.hoverOpened && event.target.closest('[data-mega-leave]')) scheduleClose(220);
    });

    document.addEventListener('mouseout', function (event) {
      var trigger = event.target.closest('[data-mega-trigger]');
      if (trigger && !trigger.contains(event.relatedTarget)) clearTimeout(openTimer);

      var mega = activeMega();
      if (!mega || !Drawers.hoverOpened) return;
      if (mega.contains(event.target) && !mega.contains(event.relatedTarget)) scheduleClose(260);
    });
  }

  /* ==========================================================================
     Announcement bar: close for the rest of the visit
     ========================================================================== */
  var ANNOUNCEMENT_KEY = 'amach-announcement-dismissed';

  function initAnnouncementClose() {
    var bar = $('[data-announcement-dismissible]');
    if (!bar || (window.Shopify && window.Shopify.designMode)) return;
    try {
      if (window.sessionStorage.getItem(ANNOUNCEMENT_KEY) === '1') bar.classList.add('is-dismissed');
    } catch (e) {}
  }

  document.addEventListener('click', function (event) {
    var close = event.target.closest('[data-announcement-close]');
    if (!close) return;
    var bar = close.closest('[data-announcement]');
    if (bar) bar.classList.add('is-dismissed');
    try {
      window.sessionStorage.setItem(ANNOUNCEMENT_KEY, '1');
    } catch (e) {}
    setHeaderVars();
  });

  /* ==========================================================================
     Footer columns: open on desktop, tap-to-open on phones
     ========================================================================== */
  var footerQuery = window.matchMedia('(min-width: 990px)');

  function syncFooterColumns(scope) {
    $$('[data-footer-accordion]', scope).forEach(function (details) {
      details.open = footerQuery.matches;
    });
  }

  if (footerQuery.addEventListener) {
    footerQuery.addEventListener('change', function () {
      syncFooterColumns();
    });
  }

  /* ==========================================================================
     Product card image swipe (phones): keep the little dashes in sync
     ========================================================================== */
  document.addEventListener(
    'scroll',
    function (event) {
      var track = event.target;
      if (!track || track.nodeType !== 1 || !track.hasAttribute('data-card-track')) return;
      if (track._raf) return;
      track._raf = requestAnimationFrame(function () {
        track._raf = null;
        var index = Math.round(track.scrollLeft / (track.clientWidth || 1));
        $$('.card__dot', track.parentElement).forEach(function (dot, i) {
          dot.classList.toggle('is-active', i === index);
        });
      });
    },
    true
  );

  /* ==========================================================================
     Scroll rail (story carousel arrows)
     ========================================================================== */
  if (!customElements.get('scroll-rail')) {
    customElements.define(
      'scroll-rail',
      class ScrollRail extends HTMLElement {
        connectedCallback() {
          if (this._ready) return;
          this._ready = true;
          this.rail = this.querySelector('[data-rail]');
          this.prev = this.querySelector('[data-rail-prev]');
          this.next = this.querySelector('[data-rail-next]');
          if (!this.rail) return;
          var self = this;
          if (this.prev) {
            this.prev.addEventListener('click', function () {
              self.go(-1);
            });
          }
          if (this.next) {
            this.next.addEventListener('click', function () {
              self.go(1);
            });
          }
          this.rail.addEventListener('scroll', debounce(this.update.bind(this), 60), { passive: true });
          window.addEventListener('resize', debounce(this.update.bind(this), 150));
          this.update();
        }

        step() {
          var card = this.rail.firstElementChild;
          return card ? card.getBoundingClientRect().width : this.rail.clientWidth;
        }

        go(direction) {
          this.rail.scrollBy({ left: direction * this.step(), behavior: 'smooth' });
        }

        update() {
          var max = this.rail.scrollWidth - this.rail.clientWidth - 2;
          if (this.prev) this.prev.hidden = this.rail.scrollLeft <= 2;
          if (this.next) this.next.hidden = max <= 0 || this.rail.scrollLeft >= max;
        }
      }
    );
  }

  /* Collection links row: bring the current collection into view on small screens. */
  function revealActiveCollectionLink(scope) {
    $$('.coll-nav', scope).forEach(function (nav) {
      var active = nav.querySelector('.coll-nav__link.is-active');
      if (!active || nav.scrollWidth <= nav.clientWidth) return;
      nav.scrollLeft = Math.max(0, active.offsetLeft - nav.clientWidth / 2 + active.offsetWidth / 2);
    });
  }

  /* ==========================================================================
     Init
     ========================================================================== */
  function hoistDrawers(scope) {
    $$('[data-drawer]', scope || document).forEach(function (drawer) {
      if (drawer.parentElement !== document.body) {
        $$('body > [data-drawer]').forEach(function (el) {
          if (el !== drawer && el.id && el.id === drawer.id) el.remove();
        });
        document.body.appendChild(drawer);
      }
      if (!drawer.classList.contains('is-open')) drawer.setAttribute('aria-hidden', 'true');
    });
  }

  function init() {
    initAnnouncementClose();
    setHeaderVars();
    initAnnouncements();
    initMegaHover();
    syncFooterColumns();
    revealActiveCollectionLink();

    if (window.ResizeObserver) {
      var header = $('[data-header]');
      if (header) new ResizeObserver(setHeaderVars).observe(header);
    }
    window.addEventListener('resize', debounce(setHeaderVars, 150));

    // Move drawers to <body> so they sit above the sticky header, then hide them for assistive tech.
    hoistDrawers();

    // Password page: open the password drawer when the form has errors.
    var passwordError = $('#PasswordDrawer .form-message--error');
    if (passwordError) Drawers.open('PasswordDrawer');

    // Open the bag if we arrive with #bag in the URL.
    if (window.location.hash === '#bag' && $('#CartDrawer')) Drawers.open('CartDrawer');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Theme editor support */
  document.addEventListener('shopify:section:load', function (event) {
    hoistDrawers(event.target);
    syncFooterColumns(event.target);
    revealActiveCollectionLink(event.target);
    setHeaderVars();
  });
  document.addEventListener('shopify:block:select', function (event) {
    var tabs = event.target.closest('collection-tabs');
    if (tabs && event.target.hasAttribute('data-tab') && tabs.select) {
      var index = $$('[data-tab]', tabs).indexOf(event.target);
      if (index > -1) tabs.select(index);
    }
    // Show the mega menu while one of its blocks is selected in the editor.
    var mega = event.target.closest('[data-mega]');
    var megaId = mega ? mega.id : event.target.hasAttribute('data-mega-trigger') ? event.target.getAttribute('data-drawer-open') : null;
    if (megaId && (!Drawers.active || Drawers.active.id !== megaId)) Drawers.open(megaId, null);
  });
  document.addEventListener('shopify:block:deselect', function (event) {
    if (Drawers.active && Drawers.active.hasAttribute('data-mega') && (event.target.closest('[data-mega]') || event.target.hasAttribute('data-mega-trigger'))) {
      Drawers.close(true);
      document.body.classList.remove('is-locked');
      var overlay = Drawers.overlay();
      if (overlay) {
        overlay.classList.remove('is-visible');
        overlay.hidden = true;
      }
    }
  });
})();
